import { useEffect, useRef, useState } from "react";
import { useInView } from "framer-motion";

interface AnimatedCounterProps {
  target: string; // e.g. "20+", "$30M+", "24/7", "99.9%"
  className?: string;
}

const AnimatedCounter = ({ target, className = "" }: AnimatedCounterProps) => {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true });
  const [display, setDisplay] = useState("0");

  useEffect(() => {
    if (!isInView) return;

    // Parse target: extract numeric part, prefix, suffix
    const match = target.match(/^([^0-9]*)([0-9.]+)(.*)$/);
    if (!match) {
      setDisplay(target);
      return;
    }

    const prefix = match[1];
    const numStr = match[2];
    const suffix = match[3];
    const targetNum = parseFloat(numStr);
    const hasDecimal = numStr.includes(".");
    const decimalPlaces = hasDecimal ? numStr.split(".")[1].length : 0;

    const duration = 1500;
    const startTime = Date.now();

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = eased * targetNum;

      if (hasDecimal) {
        setDisplay(`${prefix}${current.toFixed(decimalPlaces)}${suffix}`);
      } else {
        setDisplay(`${prefix}${Math.floor(current)}${suffix}`);
      }

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setDisplay(target);
      }
    };

    animate();
  }, [isInView, target]);

  // Special case for non-numeric targets like "24/7"
  const isNumeric = /[0-9]/.test(target);

  if (!isNumeric) {
    return <span ref={ref} className={className}>{target}</span>;
  }

  return <span ref={ref} className={className}>{display}</span>;
};

export default AnimatedCounter;
